import { ChatViewContextGetter } from '@shared/contexts/ChatWindowContext';
import { useEmailThreadContext } from '@shared/contexts/pluginContexts/EmailThreadContext';
import { useRelatedExternalMessages } from '@shared/plugin/pages/Home/components/email-thread-activity/hooks/useRelatedExternalMessages';
import { useCallback, useRef } from 'react';
import { ChatsPageWithEmailFeed } from './ChatsPageWithEmailFeed';
import { collectEmailThreadEntityIds } from './collectEmailThreadEntityIds';
import { PluginChatsPage } from './PluginChatsPage';
import { useEmailThreadChat } from './useEmailThreadChat';
import { useGetOpenEmailMessageId } from './useGetOpenEmailMessageId';
import { useRefetchEmailThreadJobs } from './useRefetchEmailThreadJobs';
import { waitForLoadedValue } from './waitForLoadedValue';

export function EmailThreadChatsPage() {
  const { threadJobs } = useEmailThreadContext();
  const threadJobsRef = useRef(threadJobs);
  threadJobsRef.current = threadJobs;
  const {
    hasFailed: hasMessagesFailed,
    loading: isLoadingMessages,
    messages,
  } = useRelatedExternalMessages();
  const emailThreadChat = useEmailThreadChat({
    hasMessagesFailed,
    isLoadingMessages,
    messageIds: messages.map((message) => message.id),
  });
  const refetchEmailThreadJobs = useRefetchEmailThreadJobs();
  const getOpenEmailMessageId = useGetOpenEmailMessageId({
    isLoadingMessages,
    messages,
  });
  const getViewContext = useCallback<ChatViewContextGetter>(async () => {
    const [jobs, openEmailMessageId] = await Promise.all([
      waitForLoadedValue({
        getSnapshot: () => ({
          value: threadJobsRef.current.jobs,
          loading: threadJobsRef.current.loading,
        }),
      }),
      getOpenEmailMessageId(),
    ]);
    return {
      entityIds: collectEmailThreadEntityIds(jobs),
      ...(openEmailMessageId ? { openEmailMessageId } : {}),
    };
  }, [getOpenEmailMessageId]);
  // NOTE(wassim): Recreated on every render so suggestions recheck the page
  // once the open email or the thread's jobs load.
  const getSuggestionContext: ChatViewContextGetter = () => getViewContext();

  return (
    <ChatsPageWithEmailFeed>
      <PluginChatsPage
        pageChat={emailThreadChat}
        getSuggestionContext={getSuggestionContext}
        getViewContext={getViewContext}
        onNewMessageInSelectedConversation={refetchEmailThreadJobs}
      />
    </ChatsPageWithEmailFeed>
  );
}
